import React, { useState } from 'react';
import { Button, Collapse, Spinner, Table } from 'react-bootstrap';
import { useLanguage } from '../../context/LanguageContext';
import api from '../../services/api';

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeReportCard(raw) {
  if (!raw || typeof raw !== 'object') return null;
  return {
    ...raw,
    charge_lines: asArray(raw.charge_lines),
    charge_rows: asArray(raw.charge_rows),
    daily_breakdown: asArray(raw.daily_breakdown),
    metrics: asArray(raw.metrics),
    by_store: asArray(raw.by_store).map((store) => ({
      ...store,
      charge_lines: asArray(store.charge_lines),
      metrics: asArray(store.metrics),
    })),
    payment: raw.payment
      ? { ...raw.payment, stores: asArray(raw.payment.stores) }
      : null,
    weekly: raw.weekly
      ? {
          ...raw.weekly,
          highlights: asArray(raw.weekly.highlights),
          four_week_trend: asArray(raw.weekly.four_week_trend),
        }
      : null,
  };
}

const BILLING_ALIPAY_UI = process.env.REACT_APP_BILLING_ALIPAY_UI !== 'false';

function DeltaText({ metric, vsLabel }) {
  const pct = metric?.pct_change;
  if (pct == null) return null;
  const cls = metric.direction === 'up' ? 'chat-report-delta-up' : (
    metric.direction === 'down' ? 'chat-report-delta-down' : ''
  );
  const sign = pct > 0 ? '+' : '';
  return (
    <span className={`chat-report-delta ${cls}`}>
      {sign}{pct}% {vsLabel}
    </span>
  );
}

function FinancialSummary({ financial, insights, sectionLabels }) {
  if (!financial) return null;
  const labels = financial.labels || {};
  const comparison = insights?.comparison || {};
  return (
    <div className="chat-report-card-financial">
      <div className="chat-report-card-financial-item">
        <div className="chat-report-card-financial-value">¥{financial.total_charges}</div>
        <div className="chat-report-card-financial-label">{labels.total_charged}</div>
        <DeltaText metric={comparison.total_charges} vsLabel={sectionLabels.vs_prev} />
      </div>
      {(financial.topups_count > 0 || Number(financial.total_topups) > 0) && (
        <div className="chat-report-card-financial-item">
          <div className="chat-report-card-financial-value">
            {financial.topups_count} / ¥{financial.total_topups}
          </div>
          <div className="chat-report-card-financial-label">{labels.topups}</div>
        </div>
      )}
      <div className={`chat-report-card-financial-item ${financial.balance_variant || ''}`}>
        <div className="chat-report-card-financial-value">¥{financial.balance}</div>
        <div className="chat-report-card-financial-label">{labels.balance}</div>
      </div>
    </div>
  );
}

function TopCharges({ items, sectionLabels }) {
  if (!items?.length) return null;
  return (
    <div className="chat-report-card-section">
      <div className="chat-report-card-section-title">{sectionLabels.top_charges}</div>
      {items.map((row) => (
        <div key={row.type || row.label} className="chat-report-top-line">
          <span>{row.label}</span>
          <strong>¥{row.amount}</strong>
          <span className="text-muted">({row.share_pct}%)</span>
        </div>
      ))}
    </div>
  );
}

function ReturnsBlock({ data, sectionLabels }) {
  if (!data || data.error) return null;
  const rows = [
    { key: 'total', label: sectionLabels.returns_total, value: data.total_returns },
    { key: 'wb', label: sectionLabels.returns_wb, value: data.wb_received_completed },
    { key: 'ours', label: sectionLabels.returns_ours, value: data.our_receipts },
    { key: 'debt', label: sectionLabels.returns_debt, value: data.debt_count, danger: data.debt_count > 0 },
    { key: 'new', label: sectionLabels.returns_new, value: data.new_in_period },
    { key: 'await', label: sectionLabels.returns_awaiting, value: data.awaiting_receipt_in_period },
  ];
  return (
    <div className="chat-report-card-section">
      <div className="chat-report-card-section-title">{sectionLabels.returns_title}</div>
      <div className="chat-report-mini-grid">
        {rows.map((r) => (
          <div key={r.key} className={`chat-report-mini-item ${r.danger ? 'chat-report-mini-item--danger' : ''}`}>
            <div className="chat-report-mini-value">{r.value ?? 0}</div>
            <div className="chat-report-mini-label">{r.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OperationsBlock({
  data,
  sectionLabels,
  onNavigateReturns,
  onExportSubmittedSale,
  saleExcelLoading,
  excelLabel,
}) {
  if (!data) return null;
  return (
    <div className="chat-report-card-section">
      <div className="chat-report-card-section-title">{sectionLabels.operations_title}</div>
      <div className="chat-report-mini-grid chat-report-mini-grid--2">
        <div className="chat-report-mini-item chat-report-mini-item--awaiting chat-report-mini-item--awaiting-hot">
          <div className="chat-report-mini-value">{data.awaiting_seller_decision ?? 0}</div>
          <div className="chat-report-mini-label">{sectionLabels.awaiting_seller}</div>
          {onNavigateReturns && (data.awaiting_seller_decision ?? 0) > 0 && (
            <Button
              size="sm"
              variant="outline-secondary"
              className="chat-report-mini-action mt-2"
              onClick={() => onNavigateReturns({ subTab: 'pendingDecision' })}
            >
              {sectionLabels.open_returns}
            </Button>
          )}
        </div>
        <div className="chat-report-mini-item">
          <div className="chat-report-mini-value">{data.submitted_for_sale_in_period ?? 0}</div>
          <div className="chat-report-mini-label">{sectionLabels.submitted_sale}</div>
          {onExportSubmittedSale && (data.submitted_for_sale_in_period ?? 0) > 0 && (
            <Button
              size="sm"
              variant="outline-success"
              className="chat-report-mini-action mt-2"
              disabled={saleExcelLoading}
              onClick={onExportSubmittedSale}
            >
              {saleExcelLoading ? <Spinner size="sm" animation="border" /> : (excelLabel || 'Excel')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function ChargeLinesTable({
  lines,
  tableLabels,
  compact = false,
  onExportExcel,
  excelLoading,
  excelLabel,
  onLineClick,
  sectionLabels,
}) {
  if (!lines?.length) return null;
  const labels = tableLabels || {};
  return (
    <div className={`chat-report-card-breakdown ${compact ? 'chat-report-card-breakdown--compact' : ''}`}>
      {!compact && labels.breakdown && (
        <div className="chat-report-card-breakdown-title">
          <span className="chat-report-card-breakdown-title-text">{labels.breakdown}</span>
          {onExportExcel && (
            <Button
              size="sm"
              variant="outline-success"
              className="chat-report-card-breakdown-export"
              disabled={excelLoading}
              onClick={onExportExcel}
            >
              {excelLoading ? <Spinner size="sm" animation="border" /> : (excelLabel || 'Excel')}
            </Button>
          )}
        </div>
      )}
      <Table size="sm" responsive className="chat-report-card-table mb-0">
        <thead>
          <tr>
            <th>{labels.service}</th>
            <th className="text-end chat-report-col-numeric">{labels.count}</th>
            <th className="text-end chat-report-col-numeric">{labels.amount}</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((row) => (
            <tr
              key={row.type || row.label}
              className={onLineClick && row.type ? 'chat-report-clickable-row' : ''}
              onClick={() => onLineClick && row.type && onLineClick(row.type)}
              title={onLineClick && row.type ? sectionLabels.open_deductions : undefined}
            >
              <td>
                {row.label}
                {row.share_pct > 0 && (
                  <span className="text-muted ms-1">({row.share_pct}%)</span>
                )}
              </td>
              <td className="text-end chat-report-col-numeric">{row.count}</td>
              <td className="text-end chat-report-col-numeric">¥{row.amount}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

function OperationsTable({ rows, sectionLabels, show, onToggle }) {
  if (!rows?.length) return null;
  return (
    <div className="chat-report-card-section">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="chat-report-card-section-title mb-0">
          {sectionLabels.all_operations} ({rows.length})
        </div>
        <Button size="sm" variant="outline-secondary" onClick={onToggle}>
          {show ? sectionLabels.hide_operations : sectionLabels.show_operations}
        </Button>
      </div>
      <Collapse in={show}>
        <div>
          <Table size="sm" responsive className="chat-report-card-table mb-0">
            <thead>
              <tr>
                <th>{sectionLabels.date || 'Date'}</th>
                <th>{sectionLabels.service}</th>
                <th>{sectionLabels.barcode || 'Barcode'}</th>
                <th className="text-end chat-report-col-numeric">{sectionLabels.amount}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id || `${row.date}-${row.barcode}-${row.amount}`}>
                  <td>{row.date}</td>
                  <td>{row.type_label || row.type}</td>
                  <td className="text-truncate" style={{ maxWidth: '8rem' }}>{row.barcode}</td>
                  <td className="text-end chat-report-col-numeric">¥{row.amount}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Collapse>
    </div>
  );
}

function DailyBreakdown({ rows, sectionLabels }) {
  if (!rows?.length) return null;
  return (
    <div className="chat-report-card-section">
      <div className="chat-report-card-section-title">{sectionLabels.daily_breakdown}</div>
      <Table size="sm" responsive className="chat-report-card-table mb-0">
        <thead>
          <tr>
            <th>{sectionLabels.date || 'Date'}</th>
            <th className="text-end chat-report-col-numeric">{sectionLabels.count}</th>
            <th className="text-end chat-report-col-numeric">{sectionLabels.amount}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.date}>
              <td>{row.date}</td>
              <td className="text-end chat-report-col-numeric">{row.operations_count}</td>
              <td className="text-end chat-report-col-numeric">¥{row.total_charges}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

function WeeklyExtras({ weekly, sectionLabels }) {
  if (!weekly) return null;
  const { highlights, four_week_trend: trend } = weekly;
  if (!highlights?.length && !trend?.length) return null;
  return (
    <div className="chat-report-card-section">
      {highlights?.length > 0 && (
        <>
          <div className="chat-report-card-section-title">{sectionLabels.weekly_highlights}</div>
          <ul className="chat-report-highlights">
            {highlights.map((text) => (
              <li key={text}>{text}</li>
            ))}
          </ul>
        </>
      )}
      {trend?.length > 0 && (
        <>
          <div className="chat-report-card-section-title mt-2">{sectionLabels.four_week_trend}</div>
          <Table size="sm" responsive className="chat-report-card-table mb-0">
            <thead>
              <tr>
                <th>{sectionLabels.period || 'Period'}</th>
                <th className="text-end chat-report-col-numeric">{sectionLabels.count}</th>
                <th className="text-end chat-report-col-numeric">{sectionLabels.amount}</th>
              </tr>
            </thead>
            <tbody>
              {trend.map((row) => (
                <tr key={row.label} className={row.is_current ? 'chat-report-trend-current' : ''}>
                  <td>{row.label}</td>
                  <td className="text-end">{row.operations_count}</td>
                  <td className="text-end">¥{row.total_charges}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}
    </div>
  );
}

export default function ReportCard({ card: rawCard, onPayError, onNavigateDeductions, onNavigateReturns }) {
  const { t, language } = useLanguage();
  const [payLoading, setPayLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [saleExcelLoading, setSaleExcelLoading] = useState(false);
  const [showOperations, setShowOperations] = useState(false);

  const card = normalizeReportCard(rawCard);
  if (!card) return null;

  const sectionLabels = {
    ...(card.section_labels || {}),
    date: t('chatApp.date'),
    barcode: t('chatApp.barcode'),
    period: t('chatApp.periodFrom'),
  };

  const handleChargeClick = (type) => {
    if (!onNavigateDeductions || !card.nav) return;
    onNavigateDeductions({
      date_from: card.nav.date_from,
      date_to: card.nav.date_to,
      store_id: card.nav.store_id || undefined,
      type,
    });
  };

  const handleExportSubmittedSale = async () => {
    const nav = card.nav || {};
    setSaleExcelLoading(true);
    try {
      const res = await api.chatUiReportsSubmittedForSaleExport({
        date_from: nav.date_from,
        date_to: nav.date_to,
        store_id: nav.store_id || undefined,
        lang: language,
      });
      const blob = res?.data ?? res;
      if (!blob) throw new Error('No file');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `submitted_for_sale_${nav.date_from || 'period'}_${nav.store_id || 'all'}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      if (onPayError) onPayError(e?.message || 'Excel failed');
    } finally {
      setSaleExcelLoading(false);
    }
  };

  const handleExcel = async () => {
    const pdf = card.pdf || {};
    setExcelLoading(true);
    try {
      const res = await api.chatUiReportsActivityExport({
        date_from: pdf.date_from,
        date_to: pdf.date_to,
        date: pdf.date_from,
        store_id: pdf.store_id || undefined,
        lang: language,
      });
      const blob = res?.data ?? res;
      if (!blob) throw new Error('No file');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_${pdf.date_from || 'period'}_${pdf.store_id || 'all'}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      if (onPayError) onPayError(e?.message || 'Excel failed');
    } finally {
      setExcelLoading(false);
    }
  };

  const handlePdf = async () => {
    const pdf = card.pdf || {};
    setPdfLoading(true);
    try {
      const res = await api.chatUiReportsDailyExport({
        date_from: pdf.date_from,
        date_to: pdf.date_to,
        date: pdf.date_from,
        store_id: pdf.store_id || undefined,
        lang: language,
      });
      const blob = res?.data ?? res;
      if (!blob) throw new Error('No file');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_${pdf.date_from || 'period'}_${pdf.store_id || 'all'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      if (onPayError) onPayError(e?.message || 'PDF failed');
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePay = async () => {
    if (!BILLING_ALIPAY_UI) return;
    setPayLoading(true);
    try {
      const data = await api.chatUiBillingAlipayCombinedPagePay();
      if (data?.pay_url) {
        window.location.assign(data.pay_url);
      } else if (onPayError) {
        onPayError(t('billing.payWithAlipayPageNoUrl'));
      }
    } catch (e) {
      if (onPayError) onPayError(e?.data?.error || e?.message || 'Payment failed');
    } finally {
      setPayLoading(false);
    }
  };

  const tableLabels = card.table_labels || {};
  const legacyMetrics = !card.financial && asArray(card.metrics);
  const insights = card.insights || {};

  return (
    <div className="chat-report-card">
      <div className="chat-report-card-header">
        <div className="chat-report-card-title">{card.title}</div>
        {card.subtitle && <div className="chat-report-card-subtitle">{card.subtitle}</div>}
        <div className="chat-report-card-meta">
          {card.period && <span>{card.period}</span>}
          {card.scope && <span className="chat-report-card-scope">{card.scope}</span>}
        </div>
      </div>

      {card.empty ? (
        <p className="chat-report-card-empty">{card.empty_text || t('chatApp.noData')}</p>
      ) : (
        <>
          {card.financial ? (
            <FinancialSummary
              financial={card.financial}
              insights={insights}
              sectionLabels={sectionLabels}
            />
          ) : (
            <div className="chat-report-card-metrics">
              {legacyMetrics.map((m) => (
                <div key={m.key} className={`chat-report-card-metric ${m.variant || ''}`}>
                  <div className="chat-report-card-metric-value">{m.value}</div>
                  <div className="chat-report-card-metric-label">{m.label}</div>
                  {m.sub && <div className="chat-report-card-metric-sub">{m.sub}</div>}
                </div>
              ))}
            </div>
          )}

          <TopCharges items={insights.top_charges} sectionLabels={sectionLabels} />
          <ReturnsBlock data={card.returns} sectionLabels={sectionLabels} />
          <OperationsBlock
            data={card.operations}
            sectionLabels={sectionLabels}
            onNavigateReturns={onNavigateReturns}
            onExportSubmittedSale={card.nav ? handleExportSubmittedSale : null}
            saleExcelLoading={saleExcelLoading}
            excelLabel={t('chatApp.downloadExcel')}
          />

          <ChargeLinesTable
            lines={card.charge_lines}
            tableLabels={tableLabels}
            onExportExcel={handleExcel}
            excelLoading={excelLoading}
            excelLabel={t('chatApp.downloadExcel')}
            onLineClick={onNavigateDeductions ? handleChargeClick : null}
            sectionLabels={sectionLabels}
          />

          <OperationsTable
            rows={card.charge_rows}
            sectionLabels={{ ...sectionLabels, ...tableLabels }}
            show={showOperations}
            onToggle={() => setShowOperations((v) => !v)}
          />

          <DailyBreakdown rows={card.daily_breakdown} sectionLabels={{ ...sectionLabels, ...tableLabels }} />
          <WeeklyExtras weekly={card.weekly} sectionLabels={sectionLabels} />
        </>
      )}

      {card.by_store.length > 0 && (
        <div className="chat-report-card-stores">
          <div className="chat-report-card-stores-title">{t('chatApp.reportByStore')}</div>
          {card.by_store.map((store) => (
            <div key={store.store_id} className="chat-report-card-store-block">
              <div className="chat-report-card-store-name">{store.store_name}</div>
              {(store.debt && Number(store.debt) > 0) || store.needs_payment ? (
                <div className="chat-report-store-payment small text-muted mb-2">
                  {sectionLabels.store_debt}: ¥{store.debt}
                  {' · '}
                  {sectionLabels.store_deposit}: ¥{store.deposit_part}
                  {store.needs_payment && (
                    <>
                      {' · '}
                      {sectionLabels.store_to_pay}: <strong>¥{store.topup_amount}</strong>
                    </>
                  )}
                </div>
              ) : null}
              {store.financial ? (
                <>
                  <FinancialSummary financial={store.financial} sectionLabels={sectionLabels} />
                  <ChargeLinesTable
                    lines={store.charge_lines}
                    tableLabels={tableLabels}
                    compact
                    onLineClick={onNavigateDeductions ? (type) => onNavigateDeductions({
                      date_from: card.nav?.date_from,
                      date_to: card.nav?.date_to,
                      store_id: store.store_id,
                      type,
                    }) : null}
                    sectionLabels={sectionLabels}
                  />
                </>
              ) : (
                <div className="chat-report-card-metrics chat-report-card-metrics--compact">
                  {asArray(store.metrics).map((m) => (
                    <div key={`${store.store_id}-${m.key}`} className={`chat-report-card-metric ${m.variant || ''}`}>
                      <div className="chat-report-card-metric-value">{m.value}</div>
                      <div className="chat-report-card-metric-label">{m.label}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {card.payment && (
        <div className="chat-report-card-payment">
          <div className="chat-report-card-payment-total">
            {t('chatApp.paymentTotal')}: ¥{card.payment.total_topup}
          </div>
          {card.payment.stores.map((row) => (
            <div key={row.store_pk} className="chat-report-card-payment-line">
              <span>{row.store_name}</span>
              <span>
                {t('chatApp.paymentDebt')}: ¥{row.debt} · {t('chatApp.paymentDeposit')}: ¥{row.deposit_part}
              </span>
              <strong>¥{row.topup_amount}</strong>
            </div>
          ))}
          {card.payment.note && (
            <p className="chat-report-card-payment-note">{card.payment.note}</p>
          )}
          {BILLING_ALIPAY_UI && (
            <Button
              size="sm"
              className="btn-gradient mt-2"
              disabled={payLoading}
              onClick={handlePay}
            >
              {payLoading ? <Spinner size="sm" animation="border" /> : (card.payment.pay_label || t('billing.payWithAlipay'))}
            </Button>
          )}
        </div>
      )}

      {card.truncated_note && (
        <p className="chat-report-card-truncated">{card.truncated_note}</p>
      )}

      <div className="chat-report-card-actions">
        <Button size="sm" variant="outline-success" disabled={pdfLoading} onClick={handlePdf}>
          {pdfLoading ? <Spinner size="sm" animation="border" /> : t('chatApp.downloadPdf')}
        </Button>
      </div>
    </div>
  );
}
